import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { BLOCK_REGISTRY, DEFAULT_LAYOUT } from './BlockRenderer';
import { portfolioReportsAPI } from '../../services/api';

const SECTION_META = {
  intro_text:                    { label: 'Introduction' },
  market_overview:               { label: 'Market Overview', hasCallouts: true },
  performance_review:            { label: 'Portfolio Performance & Review', hasCallouts: true },
  corporate_debt_review:         { label: 'Corporate Debt', hasCallouts: true },
  government_bonds_review:       { label: 'Government Bonds', hasCallouts: true },
  international_equities_review: { label: 'International Equities', hasCallouts: true },
  australian_equities_review:    { label: 'Australian Equities', hasCallouts: true },
  real_assets_review:            { label: 'Real Assets', hasCallouts: true },
  global_alpha_review:           { label: 'Global Alpha' },
  outlook:                       { label: 'Outlook', hasCallouts: true },
};

/**
 * Build the rows for the propagation grid from the source report's formData.
 * Each row: { id, label, keys[], isPerformance }
 */
function buildRows(formData) {
  const rows = [];

  // 1. Page Layout & Structure meta-row
  rows.push({
    id: '__layout__',
    label: 'Page Layout & Structure',
    keys: ['page_layout', 'custom_blocks', 'heading_overrides', 'heading_styles'],
    isPerformance: false,
  });

  const layout = formData.page_layout || DEFAULT_LAYOUT;

  layout.forEach((page) => {
    page.blocks.forEach((b) => {
      const blockId = typeof b === 'string' ? b : b.id;
      const regEntry = BLOCK_REGISTRY[blockId];
      const customDef = (formData.custom_blocks || {})[blockId];
      const def = regEntry || customDef || {};
      const label = def.label || blockId;

      if (blockId === 'title_box') {
        rows.push({
          id: blockId,
          label: 'Title Box',
          keys: ['title_box'],
          isPerformance: false,
        });
        return;
      }

      // Fixed text block
      if (regEntry && regEntry.contentKey) {
        const contentKey = regEntry.contentKey;
        const keys = [contentKey];
        if (SECTION_META[contentKey]?.hasCallouts) {
          keys.push(`${contentKey}_callouts`);
        }
        rows.push({
          id: blockId,
          label,
          keys,
          isPerformance: contentKey === 'performance_review',
        });
        return;
      }

      // Custom text block
      if (customDef && customDef.type === 'text') {
        rows.push({
          id: blockId,
          label: customDef.label || blockId,
          keys: [`custom_content.${blockId}`],
          isPerformance: false,
        });
        return;
      }

      // Custom image block
      if (customDef && customDef.type === 'image') {
        rows.push({
          id: blockId,
          label: customDef.label || 'Image',
          keys: [`custom_content.${blockId}`],
          isPerformance: false,
        });
        return;
      }

      // Shape blocks — carried by "Page Layout & Structure" row (config only).
      // Data/chart blocks have no editable content — they're carried
      // by the "Page Layout & Structure" row, so no separate row needed.
    });
  });

  // Deduplicate by id
  const seen = new Set();
  return rows.filter(r => {
    if (seen.has(r.id)) return false;
    seen.add(r.id);
    return true;
  });
}

const PropagateModal = ({ portfolios, currentPortfolioCode, quarter, domain, formData, onClose, onComplete }) => {
  const rows = useMemo(() => buildRows(formData), [formData]);

  // Available quarters for the target quarter selector
  const [availableQuarters, setAvailableQuarters] = useState([]);
  const [targetQuarter, setTargetQuarter] = useState(quarter);

  // Target brand selector — allows propagating to a different brand's portfolios
  const [brands, setBrands] = useState(null);
  const [targetBrand, setTargetBrand] = useState(domain || '_default');

  useEffect(() => {
    portfolioReportsAPI.getBrands()
      .then(res => setBrands(res.brands || []))
      .catch(() => setBrands([]));
  }, []);

  // Load portfolios for the selected target brand
  const [brandPortfolios, setBrandPortfolios] = useState(null);
  const [loadingBrandPortfolios, setLoadingBrandPortfolios] = useState(false);
  useEffect(() => {
    if (targetBrand === '_default') {
      setBrandPortfolios(null);
      return;
    }
    setLoadingBrandPortfolios(true);
    portfolioReportsAPI.getBrandPortfolios(targetBrand)
      .then(res => setBrandPortfolios(res.portfolios || []))
      .catch(() => setBrandPortfolios([]))
      .finally(() => setLoadingBrandPortfolios(false));
  }, [targetBrand]);

  const effectivePortfolios = brandPortfolios || portfolios;

  useEffect(() => {
    portfolioReportsAPI.getAvailableQuarters('both')
      .then(res => {
        const periods = (res.periods || []).map(p => p.value);
        setAvailableQuarters(periods);
      })
      .catch(() => {});
  }, []);

  // When targeting a different quarter or brand, include the current portfolio as a valid target
  const isCrossQuarter = targetQuarter !== quarter;
  const isCrossBrand = targetBrand !== (domain || '_default');

  const targetPortfolios = useMemo(
    () => (isCrossQuarter || isCrossBrand)
      ? effectivePortfolios  // all portfolios including self
      : effectivePortfolios.filter(p => p.portfolio_code !== currentPortfolioCode),
    [effectivePortfolios, currentPortfolioCode, isCrossQuarter, isCrossBrand]
  );

  // Checkbox state: { [rowId]: { [portfolioCode]: bool } }
  // Everything starts unticked — propagation is opt-in per section/portfolio
  const [checked, setChecked] = useState(() => {
    const init = {};
    const targets = effectivePortfolios.filter(p => p.portfolio_code !== currentPortfolioCode);
    rows.forEach(row => {
      init[row.id] = {};
      targets.forEach(p => {
        init[row.id][p.portfolio_code] = false;
      });
    });
    return init;
  });

  // When target quarter, brand or portfolio list changes, re-initialise checkboxes
  useEffect(() => {
    setChecked(() => {
      const init = {};
      const targets = (isCrossQuarter || isCrossBrand)
        ? effectivePortfolios
        : effectivePortfolios.filter(p => p.portfolio_code !== currentPortfolioCode);
      rows.forEach(row => {
        if (row.isData) return;
        init[row.id] = {};
        targets.forEach(p => {
          init[row.id][p.portfolio_code] = false;
        });
      });
      return init;
    });
  }, [targetQuarter, targetBrand, effectivePortfolios]); // eslint-disable-line react-hooks/exhaustive-deps

  const [propagating, setPropagating] = useState(false);
  const [result, setResult] = useState(null);

  const toggleCell = useCallback((rowId, pCode) => {
    setChecked(prev => ({
      ...prev,
      [rowId]: { ...prev[rowId], [pCode]: !prev[rowId]?.[pCode] },
    }));
  }, []);

  const toggleRow = useCallback((rowId) => {
    setChecked(prev => {
      const rowState = prev[rowId] || {};
      const allOn = targetPortfolios.every(p => rowState[p.portfolio_code]);
      const newRowState = {};
      targetPortfolios.forEach(p => { newRowState[p.portfolio_code] = !allOn; });
      return { ...prev, [rowId]: newRowState };
    });
  }, [targetPortfolios]);

  const toggleColumn = useCallback((pCode) => {
    setChecked(prev => {
      const selectableRows = rows;
      const allOn = selectableRows.every(r => prev[r.id]?.[pCode]);
      const next = { ...prev };
      selectableRows.forEach(r => {
        next[r.id] = { ...next[r.id], [pCode]: !allOn };
      });
      return next;
    });
  }, [rows]);

  const toggleAll = useCallback(() => {
    setChecked(prev => {
      const selectableRows = rows;
      const allOn = selectableRows.every(r =>
        targetPortfolios.every(p => prev[r.id]?.[p.portfolio_code])
      );
      const next = {};
      selectableRows.forEach(r => {
        next[r.id] = {};
        targetPortfolios.forEach(p => { next[r.id][p.portfolio_code] = !allOn; });
      });
      return next;
    });
  }, [rows, targetPortfolios]);

  // Count how many portfolios will receive at least one section
  const affectedPortfolios = useMemo(() => {
    const set = new Set();
    rows.forEach(row => {
      targetPortfolios.forEach(p => {
        if (checked[row.id]?.[p.portfolio_code]) set.add(p.portfolio_code);
      });
    });
    return set.size;
  }, [checked, rows, targetPortfolios]);

  const handlePropagate = async () => {
    setPropagating(true);
    setResult(null);

    // Build per-portfolio section keys
    const perPortfolio = {};
    targetPortfolios.forEach(p => {
      const keys = new Set();
      rows.forEach(row => {
        if (row.isData) return;
        if (checked[row.id]?.[p.portfolio_code]) {
          row.keys.forEach(k => keys.add(k));
        }
      });
      if (keys.size > 0) {
        perPortfolio[p.portfolio_code] = [...keys];
      }
    });

    // Group portfolios by identical section key sets to minimise API calls
    const groups = {};
    for (const [pCode, keys] of Object.entries(perPortfolio)) {
      const sortedKey = keys.sort().join('|');
      if (!groups[sortedKey]) groups[sortedKey] = { sections: keys, portfolios: [] };
      groups[sortedKey].portfolios.push(pCode);
    }

    let totalCount = 0;
    let allFailures = [];

    try {
      for (const group of Object.values(groups)) {
        const res = await portfolioReportsAPI.propagateContent({
          sourcePortfolio: currentPortfolioCode,
          quarter,
          targetQuarter: isCrossQuarter ? targetQuarter : null,
          domain: domain || '_default',
          targetDomain: targetBrand !== (domain || '_default') ? targetBrand : null,
          targetPortfolios: group.portfolios,
          sections: group.sections,
        });
        totalCount += res.count;
        if (res.failures?.length) allFailures = [...allFailures, ...res.failures];
      }
      setResult({ success: true, count: totalCount, failures: allFailures });
      if (allFailures.length === 0 && onComplete) onComplete();
    } catch (err) {
      setResult({ success: false, error: err.response?.data?.detail || 'Error propagating content' });
    } finally {
      setPropagating(false);
    }
  };

  return createPortal(
    <div className="propagate-modal-overlay" onClick={onClose}>
      <div className="propagate-modal" onClick={e => e.stopPropagation()}>
        <div className="propagate-modal-header">
          <h3>Propagate Content</h3>
          <button className="propagate-modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="propagate-modal-controls">
          <label className="propagate-quarter-selector">
            <span>Target Quarter</span>
            <select value={targetQuarter} onChange={e => setTargetQuarter(e.target.value)}>
              <option value={quarter}>{quarter} (current)</option>
              {availableQuarters
                .filter(q => q !== quarter)
                .map(q => (
                  <option key={q} value={q}>{q}</option>
                ))}
            </select>
          </label>
          {brands && brands.length > 0 && (
            <label className="propagate-quarter-selector">
              <span>Target Brand</span>
              <select value={targetBrand} onChange={e => setTargetBrand(e.target.value)}>
                <option value="_default">Drummond</option>
                {brands.map(b => (
                  <option key={b.domain} value={b.domain}>{b.company_name}</option>
                ))}
              </select>
            </label>
          )}
          {isCrossQuarter && (
            <span className="propagate-cross-quarter-note">
              Propagating from {quarter} to {targetQuarter} — current portfolio is included as a target.
            </span>
          )}
          {targetBrand !== (domain || '_default') && (
            <span className="propagate-cross-quarter-note">
              Propagating to {targetBrand === '_default' ? 'Drummond' : targetBrand} brand.
            </span>
          )}
        </div>

        <p className="propagate-modal-desc">
          Select which content sections to copy to which portfolios.
        </p>

        {result && (
          <div className={`propagate-result ${result.success ? 'success' : 'error'}`}>
            {result.success
              ? `Successfully propagated to ${result.count} portfolio${result.count !== 1 ? 's' : ''}.`
              : result.error}
            {result.failures?.length > 0 && (
              <div className="propagate-failures">
                Failed: {result.failures.map(f => f.portfolio_code).join(', ')}
              </div>
            )}
          </div>
        )}

        <div className="propagate-grid-wrapper">
          <table className="propagate-grid">
            <thead>
              <tr>
                <th className="propagate-corner">
                  <button className="propagate-select-all" onClick={toggleAll} title="Toggle all">
                    All
                  </button>
                </th>
                {targetPortfolios.map(p => (
                  <th key={p.portfolio_code} className="propagate-col-header">
                    <button className="propagate-col-toggle" onClick={() => toggleColumn(p.portfolio_code)} title="Toggle column">
                      {p.portfolio_code}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.id}>
                  <td className="propagate-row-label">
                    <button className="propagate-row-toggle" onClick={() => toggleRow(row.id)} title="Toggle row">
                      {row.label}
                    </button>
                  </td>
                  {targetPortfolios.map(p => (
                    <td key={p.portfolio_code} className="propagate-cell">
                      <input
                        type="checkbox"
                        checked={!!checked[row.id]?.[p.portfolio_code]}
                        onChange={() => toggleCell(row.id, p.portfolio_code)}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="propagate-modal-footer">
          <button className="btn-cancel" onClick={onClose}>Cancel</button>
          <button
            className="btn-propagate"
            onClick={handlePropagate}
            disabled={propagating || affectedPortfolios === 0}
          >
            {propagating
              ? 'Propagating...'
              : `Propagate to ${affectedPortfolios} portfolio${affectedPortfolios !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PropagateModal;
